import { createFileRoute, Link } from '@tanstack/react-router';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import { useSWRConfig } from 'swr';
import { z } from 'zod';

import { Button } from '@apps/web/components/ui/button';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import { Spinner } from '@apps/web/components/ui/spinner';
import { Tabs, TabsList, TabsTrigger } from '@apps/web/components/ui/tabs';
import { BranchTable } from '@apps/web/routes/_app/admin/voice-config/branch_table';
import { EffectiveConfigTab } from '@apps/web/routes/_app/admin/voice-config/effective_config_tab';
import { MetricsTab } from '@apps/web/routes/_app/admin/voice-config/metrics_tab';
import { RuntimeStatusPill } from '@apps/web/routes/_app/admin/voice-config/voice_config_shared';
import { useAdminOrganizationVoiceConfig } from '@apps/web/swr_data_fetching/admin_voice_config_queries';

const searchSchema = z.object({
    tab: z
        .enum(['configuration', 'branches', 'metrics'])
        .default('configuration'),
    days: z
        .union([z.literal(7), z.literal(30), z.literal(90)])
        .default(7)
        .catch(7),
});

type VoiceConfigTab = z.infer<typeof searchSchema>['tab'];

export const Route = createFileRoute(
    '/_app/admin/voice-config/$organization-id',
)({
    head: () => ({
        meta: [
            {
                title: 'Voice Config | Phoebe Admin',
            },
        ],
    }),
    validateSearch: searchSchema,
    component: OrganizationVoiceConfigPage,
});

function OrganizationVoiceConfigPage() {
    const { 'organization-id': organizationId } = Route.useParams();
    const navigate = Route.useNavigate();
    const { tab, days } = Route.useSearch();
    const { mutate: mutateCache } = useSWRConfig();
    const { data, isLoading, error, mutate } =
        useAdminOrganizationVoiceConfig(organizationId);

    if (isLoading) {
        return (
            <div className="flex h-full items-center justify-center gap-2">
                <Spinner className="size-4" />
                <span className="text-muted-foreground text-sm">
                    Loading voice config…
                </span>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="text-destructive flex h-full items-center justify-center text-sm">
                Could not load this organization’s voice config.
            </div>
        );
    }

    return (
        <div className="flex h-full flex-col">
            <div className="border-border space-y-3 border-b p-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <Button
                            asChild={true}
                            variant="ghost"
                            size="icon"
                            className="size-8"
                        >
                            <Link
                                to="/admin/voice-config"
                                aria-label="Back to voice config"
                            >
                                <ArrowLeft className="h-4 w-4" />
                            </Link>
                        </Button>
                        <div>
                            <h2 className="text-lg font-semibold">
                                {data.organization.name}
                            </h2>
                            <p className="text-muted-foreground text-xs">
                                {data.organization.timezone}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <RuntimeStatusPill enabled={data.runtime_enabled} />
                        <Link
                            to="/admin/organizations/$organization-id"
                            params={{ 'organization-id': organizationId }}
                            className="text-muted-foreground inline-flex items-center gap-1 text-sm underline underline-offset-2"
                        >
                            Organization
                            <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                    </div>
                </div>
                {data.quality_mode.active ? (
                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
                        <span className="font-medium">
                            Voice quality mode is on.
                        </span>{' '}
                        It overlays the LLM policy after branch overrides are
                        applied, so branch LLM changes have no effect while it
                        lasts
                        {data.quality_mode.expires_at
                            ? ` (until ${new Date(
                                  data.quality_mode.expires_at,
                              ).toLocaleString()})`
                            : ''}
                        .
                    </div>
                ) : null}
                <Tabs
                    value={tab}
                    onValueChange={(value) => {
                        void navigate({
                            search: (previous) => ({
                                ...previous,
                                tab: toVoiceConfigTab(value),
                            }),
                        });
                    }}
                >
                    <TabsList>
                        <TabsTrigger value="configuration">
                            Configuration
                        </TabsTrigger>
                        <TabsTrigger value="branches">
                            Branches
                            {data.branches.length > 0
                                ? ` (${data.branches.length})`
                                : ''}
                        </TabsTrigger>
                        <TabsTrigger value="metrics">Metrics</TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>
            <ScrollArea className="flex-1 overflow-auto">
                <div className="p-4">
                    {tab === 'configuration' ? (
                        <EffectiveConfigTab
                            effective={data.effective}
                            mainValues={data.main_values}
                            voiceSettingsPresent={data.voice_settings_present}
                            qualityModeActive={data.quality_mode.active}
                            organizationId={organizationId}
                            onConfigSaved={() => {
                                void mutate();
                                // Branch diffs are computed against the main
                                // configuration, so they go stale the moment
                                // it changes.
                                void mutateCache(isVoiceConfigBranchKey);
                            }}
                        />
                    ) : tab === 'branches' ? (
                        <BranchTable
                            organizationId={organizationId}
                            onBranchesChanged={() => {
                                void mutate();
                            }}
                        />
                    ) : (
                        <MetricsTab
                            organizationId={organizationId}
                            days={days}
                            onDaysChange={(nextDays) => {
                                void navigate({
                                    search: (previous) => ({
                                        ...previous,
                                        days: nextDays,
                                    }),
                                });
                            }}
                        />
                    )}
                </div>
            </ScrollArea>
        </div>
    );
}

/** Every branch query for any organization: rows, diffs, and metrics. */
function isVoiceConfigBranchKey(key: unknown): boolean {
    return (
        Array.isArray(key) &&
        typeof key[1] === 'string' &&
        key[1].startsWith(
            '/admin/organizations/{organization_id}/voice-config/branches',
        )
    );
}

function toVoiceConfigTab(value: string): VoiceConfigTab {
    switch (value) {
        case 'branches':
            return 'branches';
        case 'metrics':
            return 'metrics';
        default:
            return 'configuration';
    }
}
